/**
 * API Keys panel — Settings tab. Admin-only management of Bearer keys that
 * external partners (panic-button call centre, fleet systems…) use to open
 * incidents via /api/v1/public/…
 *
 * UX highlights:
 *   - Raw key is shown ONCE at creation in a modal — copy or lose it.
 *   - Existing keys display just the `pk_live_xxxx` prefix + last-used stats.
 *   - Revoke is one-click but confirms first (irreversible).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Key, Plus, Trash2, Loader2, Check, AlertTriangle } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

import api, { formatApiError } from '../../lib/api';

const SCOPES = [
  { value: 'incident:create', label: 'Create / cancel incidents', hint: 'POST /api/v1/public/incidents · /{id}/cancel · /{id}/notes' },
  { value: 'location:report', label: 'Report GPS location', hint: 'POST /api/v1/public/incidents/{id}/location' },
  { value: 'incident:read', label: 'Read incident status', hint: 'GET /api/v1/public/incidents/{id}' },
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('en-ZA', { hour12: false }) : '—');

export const ApiKeysPanel = () => {
  const [keys, setKeys] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', scopes: ['incident:create', 'location:report', 'incident:read'],
    default_customer_id: '', default_service_id: '',
    rate_limit_per_min: 60, ip_allowlist: '',
  });
  const [saving, setSaving] = useState(false);
  const [showRawKey, setShowRawKey] = useState(null); // { raw_key, name } after create
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [k, c, s] = await Promise.all([
        api.get('/api-keys'),
        api.get('/customers'),
        api.get('/services'),
      ]);
      // Active keys first — revoked ones sink to the bottom so the table stays usable.
      setKeys([...(k.data || [])].sort((a, b) => (a.revoked_at ? 1 : 0) - (b.revoked_at ? 1 : 0)));
      setCustomers(c.data || []);
      setServices(s.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const customerName = (id) => customers.find((c) => c.id === id)?.company_name || '—';

  const saveDefaultService = async (k, serviceId) => {
    try {
      await api.patch(`/api-keys/${k.id}`, { default_service_id: serviceId });
      toast.success(`Default service updated for "${k.name}"`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const activeCustomers = useMemo(() => customers.filter((c) => c.active !== false), [customers]);
  const activeServices = useMemo(() => services.filter((s) => s.active !== false), [services]);

  const resetForm = () => setForm({
    name: '', scopes: ['incident:create', 'location:report', 'incident:read'],
    default_customer_id: '', default_service_id: '',
    rate_limit_per_min: 60, ip_allowlist: '',
  });

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Give the key a descriptive name'); return; }
    if (!form.default_customer_id) { toast.error('Pick the default customer this key attributes incidents to'); return; }
    if (form.scopes.length === 0) { toast.error('Choose at least one scope'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        scopes: form.scopes,
        default_customer_id: form.default_customer_id,
        default_service_id: form.default_service_id || null,
        rate_limit_per_min: Number(form.rate_limit_per_min) || 60,
        ip_allowlist: form.ip_allowlist
          .split(/[\s,]+/).map((s) => s.trim()).filter(Boolean),
      };
      const r = await api.post('/api-keys', payload);
      setShowRawKey({ raw_key: r.data.raw_key, name: r.data.name });
      resetForm();
      setCreateOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const handleRevoke = async (k) => {
    if (!window.confirm(`Revoke API key "${k.name}"?\n\nThis is permanent. Any partner using this key will be locked out immediately.`)) return;
    try {
      await api.post(`/api-keys/${k.id}/revoke`);
      toast.success('Key revoked');
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copyRawKey = async () => {
    try {
      await navigator.clipboard.writeText(showRawKey.raw_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Copied to clipboard');
    } catch { toast.error('Copy failed — please select and copy manually'); }
  };

  return (
    <div className="space-y-4" data-testid="api-keys-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-[#0D5B69] flex items-center gap-2">
            <Key className="w-4 h-4" /> API Keys
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Bearer tokens for third-party integrations (panic buttons, fleet systems, custom dispatch).
            Each key attributes incidents to a fixed CSG customer.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="gap-1 bg-[#80C030] hover:bg-[#6FA528] text-white"
          data-testid="api-key-new-btn"
        >
          <Plus className="w-4 h-4" /> New key
        </Button>
      </div>

      <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Prefix</TableHead>
              <TableHead>Attributes to</TableHead>
              <TableHead>Primary service</TableHead>
              <TableHead>Scopes</TableHead>
              <TableHead>Rate</TableHead>
              <TableHead>Usage</TableHead>
              <TableHead>Last used</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="text-center py-6 text-slate-400 text-xs">
                <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…
              </TableCell></TableRow>
            ) : keys.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-400 text-sm">
                No API keys yet — click <b>New key</b> to issue one.
              </TableCell></TableRow>
            ) : keys.map((k, idx) => (
              <TableRow key={k.id} data-testid={`api-key-row-${idx}`}>
                <TableCell className="font-medium text-slate-800">{k.name}</TableCell>
                <TableCell className="font-mono text-xs text-slate-500">{k.key_prefix}…</TableCell>
                <TableCell className="text-xs">{customerName(k.default_customer_id)}</TableCell>
                <TableCell className="text-xs">
                  {k.revoked_at ? (
                    <span className="text-slate-400">
                      {services.find((s) => s.id === k.default_service_id)?.name || '—'}
                    </span>
                  ) : (
                    <Select
                      value={k.default_service_id || 'none'}
                      onValueChange={(v) => saveDefaultService(k, v === 'none' ? null : v)}
                    >
                      <SelectTrigger className="h-8 text-xs w-[190px]" data-testid={`api-key-default-service-${idx}`}>
                        <SelectValue placeholder="— No default —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— No default —</SelectItem>
                        {activeServices.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(k.scopes || []).map((s) => (
                      <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-xs">{k.rate_limit_per_min}/min</TableCell>
                <TableCell className="font-mono text-xs text-slate-600">{k.usage_count || 0}</TableCell>
                <TableCell className="text-xs text-slate-500">{fmtDate(k.last_used_at)}</TableCell>
                <TableCell>
                  {k.revoked_at ? (
                    <Badge variant="outline" className="text-red-700 border-red-200 bg-red-50">Revoked</Badge>
                  ) : k.active ? (
                    <Badge variant="outline" className="text-green-700 border-green-200 bg-green-50">Active</Badge>
                  ) : (
                    <Badge variant="outline" className="text-slate-500 border-slate-200 bg-slate-50">Inactive</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {!k.revoked_at && (
                    <Button
                      size="sm" variant="ghost"
                      className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                      onClick={() => handleRevoke(k)}
                      title="Revoke this key"
                      data-testid={`api-key-revoke-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* -------- Create dialog -------- */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="api-key-create-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0D5B69] flex items-center gap-2">
              <Key className="w-5 h-5" /> New API key
            </DialogTitle>
            <DialogDescription>
              Issue a new Bearer token for a third-party integration. The raw
              key will be shown to you exactly once — copy it immediately.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">Descriptive name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                placeholder="e.g. Panic Button — Prod"
                maxLength={100}
                data-testid="api-key-name-input"
              />
            </div>

            <div>
              <Label className="text-xs">Default customer *</Label>
              <Select value={form.default_customer_id}
                      onValueChange={(v) => setForm((s) => ({ ...s, default_customer_id: v }))}>
                <SelectTrigger data-testid="api-key-customer-select">
                  <SelectValue placeholder="Pick the customer incidents attribute to" />
                </SelectTrigger>
                <SelectContent>
                  {activeCustomers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400 mt-1">Every incident this key opens is attributed to this customer.</p>
            </div>

            <div>
              <Label className="text-xs">Default primary service (optional)</Label>
              <Select value={form.default_service_id || 'none'}
                      onValueChange={(v) => setForm((s) => ({ ...s, default_service_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="api-key-service-select">
                  <SelectValue placeholder="No default — operator picks after" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No default —</SelectItem>
                  {activeServices.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Scopes *</Label>
              <div className="space-y-1.5 mt-1 border border-slate-200 rounded p-2 bg-slate-50">
                {SCOPES.map((sc) => (
                  <label key={sc.value} className="flex items-start gap-2 text-xs cursor-pointer">
                    <Checkbox
                      checked={form.scopes.includes(sc.value)}
                      onCheckedChange={(checked) => setForm((s) => ({
                        ...s,
                        scopes: checked ? [...new Set([...s.scopes, sc.value])] : s.scopes.filter((x) => x !== sc.value),
                      }))}
                      data-testid={`api-key-scope-${sc.value.replace(':', '-')}`}
                    />
                    <span>
                      <b>{sc.label}</b>
                      <span className="text-slate-400 font-mono block text-[10px]">{sc.hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Rate limit (req/min)</Label>
                <Input
                  type="number" min="1" max="1000"
                  value={form.rate_limit_per_min}
                  onChange={(e) => setForm((s) => ({ ...s, rate_limit_per_min: e.target.value }))}
                  data-testid="api-key-rate-input"
                />
              </div>
              <div>
                <Label className="text-xs">IP allow-list (optional)</Label>
                <Input
                  placeholder="Blank = any IP"
                  value={form.ip_allowlist}
                  onChange={(e) => setForm((s) => ({ ...s, ip_allowlist: e.target.value }))}
                  data-testid="api-key-ip-input"
                />
                <p className="text-[10px] text-slate-400 mt-0.5">Comma/space separated IPs or CIDR ranges.</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={saving}
              className="bg-[#80C030] hover:bg-[#6FA528] text-white"
              data-testid="api-key-create-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* -------- One-time raw key reveal -------- */}
      <Dialog open={!!showRawKey} onOpenChange={(o) => { if (!o) setShowRawKey(null); }}>
        <DialogContent className="max-w-lg" data-testid="api-key-reveal-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0D5B69] flex items-center gap-2">
              <Key className="w-5 h-5" /> Key created — copy it now
            </DialogTitle>
            <DialogDescription>
              This is the ONLY time you&apos;ll see the full key. Copy it into your
              partner&apos;s config immediately. If you lose it, revoke and create a
              new one.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Treat this like a password. Anyone with it can open incidents on the CSG platform on behalf of <b>{showRawKey?.name}</b>.</span>
          </div>

          <div className="mt-3">
            <Label className="text-xs">Bearer token</Label>
            <div className="flex gap-2 mt-1">
              <Input
                readOnly
                value={showRawKey?.raw_key || ''}
                className="font-mono text-xs"
                data-testid="api-key-raw-value"
                onFocus={(e) => e.target.select()}
              />
              <Button
                type="button" variant="outline"
                onClick={copyRawKey}
                className="gap-1"
                data-testid="api-key-copy-btn"
              >
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowRawKey(null)} className="bg-[#0D5B69] hover:bg-[#08434E] text-white">
              I&apos;ve copied it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
